import { z } from 'zod';
/**
 * Schichtplanung (KON-040). Wochentage zählen wie im Kalender von 0 (Montag) bis 6 (Sonntag). Uhrzeiten sind „HH:MM“, Daten „JJJJ-MM-TT“.
 * Ein Entwurf ist nur für das Büro sichtbar, erst eine veröffentlichte Schicht sieht der Mitarbeiter in „Mein Konto“.
 */
export const SHIFT_STATUSES = ['draft', 'published'];
export const SHIFT_STATUS_LABELS = { draft: 'Entwurf', published: 'Veröffentlicht' };
export const SHIFT_SCOPES = ['week', 'month', 'rest_of_year'];
export const SHIFT_SCOPE_LABELS = { week: 'Woche', month: 'Monat', rest_of_year: 'Rest des Jahres' };
export const WEEKDAY_LABELS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
export const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
/** Wochen je Monat im Durchschnitt, um Monatsstunden in Wochenstunden umzurechnen */
export const WEEKS_PER_MONTH = 52 / 12;
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Bitte eine Uhrzeit wie 08:00 angeben');
const day = z.iso.date('Ungültiges Datum');
const note = z
    .string()
    .trim()
    .max(500);
export const shiftPositionInput = z
    .object({
    position: z.string().trim().min(1, 'Position fehlt').max(100),
    hours: z.number('Bitte eine Zahl angeben').positive('Mehr als 0 Stunden').max(300, 'Höchstens 300 Stunden'),
    period: z.enum(['week', 'month']),
})
    .strict();
export const updateShiftSettingsInput = z
    .object({
    workDays: z
        .array(z.number().int().min(0).max(6))
        .min(1, 'Mindestens ein Arbeitstag')
        .transform((l) => [...new Set(l)].sort((a, b) => a - b)),
    defaultStart: time,
    maxDailyHours: z.number('Bitte eine Zahl angeben').positive('Mehr als 0 Stunden').max(24, 'Höchstens 24 Stunden'),
    minRestHours: z.number('Bitte eine Zahl angeben').min(0, 'Nicht negativ').max(24, 'Höchstens 24 Stunden'),
    maxVacationDaysPerMonth: z.number('Bitte eine Zahl angeben').min(0, 'Nicht negativ').max(31, 'Höchstens 31 Tage').nullable(),
    positions: z
        .array(shiftPositionInput)
        .max(100)
        .refine((l) => new Set(l.map((p) => p.position.toLowerCase())).size === l.length, 'Jede Position nur einmal'),
})
    .strict();
export const generateShiftsInput = z
    .object({
    employeeId: z.uuid('Ungültiger Mitarbeiter'),
    scope: z.enum(SHIFT_SCOPES),
    /** Ein Tag in der gewünschten Woche oder im Monat. Bei „Rest des Jahres“ der erste Tag. */
    date: day,
    /** Beginn der Schicht, sonst die Vorgabe aus der Einrichtung */
    startTime: time.optional(),
})
    .strict();
export const createShiftInput = z
    .object({
    employeeId: z.uuid('Ungültiger Mitarbeiter'),
    date: day,
    startTime: time,
    endTime: time,
    breakMinutes: z.number().int().min(0).max(480).default(0),
    note: note.optional().transform((v) => v || undefined),
    /** Sofort wirksam statt als Entwurf: „Einzelne Schicht“ im Backoffice legt die Schicht veröffentlicht an */
    publish: z.boolean().default(false),
})
    .strict();
export const updateShiftInput = z
    .object({
    startTime: time.optional(),
    endTime: time.optional(),
    breakMinutes: z.number().int().min(0).max(480).optional(),
    note: note.nullable().optional().transform((v) => (v === '' ? null : v)),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const listShiftsQuery = z.object({ from: day, to: day, employeeId: z.uuid().optional(), status: z.enum(SHIFT_STATUSES).optional() });
export const publishShiftsInput = z.object({ from: day, to: day, employeeId: z.uuid().optional(), status: z.enum(SHIFT_STATUSES).default('published') }).strict();
export const SHIFT_BULK_ACTIONS = ['publish', 'withdraw', 'delete'];
export const bulkShiftsInput = z.object({ ids: z.array(z.uuid('Ungültige ID')).min(1, 'Keine Schicht ausgewählt').max(500, 'Höchstens 500 Schichten auf einmal'), action: z.enum(SHIFT_BULK_ACTIONS) }).strict();
export const SKIP_REASON_LABELS = {
    holiday: 'Feiertag',
    leave: 'Genehmigte Freistellung',
    published: 'Schon veröffentlicht',
    not_employed: 'Nicht beschäftigt',
};
/**
 * Aufgabe in den Schichtplan übernehmen: Die Aufgabe erscheint dort als Überlagerung über dem Dienst der Beteiligten (zuständige Person und Beteiligte). Voraussetzung ist ein
 * veröffentlichter Dienst am Tag der Aufgabe, in dessen Zeit die Aufgabe passt. Es entstehen keine neuen Schichten. Ohne `confirm` liefert der Server nur die Vorschau: bei wem die
 * Aufgabe passt und bei wem nicht (abwesend, kein veröffentlichter Dienst, Aufgabe außerhalb des Dienstes).
 */
export const taskToShiftsInput = z.object({ taskId: z.uuid('Ungültige Aufgabe'), confirm: z.boolean().default(false) }).strict();
export const TASK_SHIFT_BLOCK_KINDS = ['absent', 'no_shift', 'draft', 'outside', 'no_employee'];
//# sourceMappingURL=shifts.js.map